import { BaseComponentService } from 'src/app/services/_app/base-component.service';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { DepartmentUtils } from 'src/app/models/department.model';
import { LicenseService } from 'src/app/services/license.service';
import { ApiError } from 'src/app/models/api/api-error.model';
import { Component, Inject, inject } from '@angular/core';
import { AppTools } from 'src/app/utils/tools';
import {
  License,
  LicenseAutoAssignment,
} from 'src/app/models/license/license.model';

@Component({
  selector: 'app-license-dialog',
  templateUrl: './license-dialog.component.html',
})
export class LicenseDialogComponent {
  private _licenseService: LicenseService = inject(LicenseService);
  private _baseComponentService = inject(BaseComponentService);

  protected licenseId?: string = undefined;
  protected isPosting: boolean = false;

  protected headquartersListing = ['MILANO', 'GORGONZOLA'];
  protected userTypesListing = ['Student', 'Teacher', 'Parent', 'Tutor'];
  public departmentsListing: string[] = DepartmentUtils.getDepartments().map(
    (d) => d.toString().trim()
  );

  protected formGroup: FormGroup = new FormGroup({
    headquarters: new FormArray(
      this.headquartersListing.map(() => new FormControl(false))
    ),
    title: new FormControl(),
    description: new FormControl(),
    dashboard_link: new FormControl(),
    link: new FormControl(),
    private_key: new FormControl(),
    key: new FormControl(),
    available: new FormControl(),
    start: new FormControl(),
    end: new FormControl(),
    enabled: new FormControl(true),
    instructions: new FormControl(),
    auto_assignments: new FormArray([]),
  });

  get autoAssignments(): FormArray {
    return this.formGroup.get('auto_assignments') as FormArray;
  }

  constructor(
    private _dialogRef: MatDialogRef<LicenseDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.licenseId = this.data.licenseId || undefined;
    if (this.licenseId) this._getLicenseDetails();
  }

  private _createAutoAssignmentGroup(
    assignment: LicenseAutoAssignment
  ): FormGroup {
    return new FormGroup({
      headquarters: new FormArray(
        this.headquartersListing.map(
          (h) => new FormControl(assignment.headquarters?.includes(h) || false)
        )
      ),
      user_types: new FormArray(
        this.userTypesListing.map(
          (t) => new FormControl(assignment.user_types?.includes(t) || false)
        )
      ),
      departments: new FormArray(
        this.departmentsListing.map(
          (d) => new FormControl(assignment.departments?.includes(d) || false)
        )
      ),
      years: new FormArray(
        [1, 2, 3, 4, 5].map(
          (y) => new FormControl(assignment.years?.includes(y) || false)
        )
      ),
      sections: new FormArray(
        ['A', 'B', 'C'].map(
          (s) => new FormControl(assignment.sections?.includes(s) || false)
        )
      ),
    });
  }

  private async _getLicenseDetails() {
    this._licenseService.getLicenseDetails(this.licenseId).subscribe({
      next: (response) => {
        const license: License = new License(response.data);
        this.formGroup.patchValue({
          title: license.title,
          dashboard_link: license.dashboard_link,
          link: license.link,
          private_key: license.private_key,
          key: license.key,
          available: license.available,
          start: license.start?.toISOString().split('T').at(0),
          end: license.end?.toISOString().split('T').at(0),
          enabled: license.enabled,
          instructions: license.instructions,
        });
        this.formGroup.setControl(
          'headquarters',
          new FormArray(
            this.headquartersListing
              .map((sede) => license.headquarters?.includes(sede))
              .map((value) => new FormControl(value))
          )
        );
        this.formGroup.setControl(
          'auto_assignments',
          new FormArray(
            (license.auto_assignments || []).map((assignment) =>
              this._createAutoAssignmentGroup(assignment)
            )
          )
        );
      },
      error: (error) => {
        this._baseComponentService.showErrorDialog({
          error: new ApiError(error),
        });
        this._dialogRef.close(false);
      },
    });
  }

  protected onConfirm() {
    this.isPosting = true;
    const formValue = this.formGroup.value;
    const headquarters = this.headquartersListing
      .filter((_, i) => formValue.headquarters[i])
      .map((h) => h.trim().toUpperCase());
    let auto_assignments = [];
    for (let i = 0; i < formValue.auto_assignments.length; i++) {
      const configs = formValue.auto_assignments[i];
      const autoAssignmentConfigs = {
        headquarters: this.headquartersListing.filter(
          (_, i) => configs.headquarters[i]
        ),
        user_types: this.userTypesListing.filter(
          (_, i) => configs.user_types[i]
        ),
        years: [1, 2, 3, 4, 5].filter((_, i) => configs.years[i]),
        sections: ['A', 'B', 'C'].filter((_, i) => configs.sections[i]),
        departments: this.departmentsListing.filter(
          (_, i) => configs.departments[i]
        ),
      };
      let push = false;
      Object.values(autoAssignmentConfigs).forEach((value: any[]) => {
        if (value.length > 0) push = true;
      });
      if (push) auto_assignments.push(autoAssignmentConfigs);
    }
    const data = AppTools.removeNullAndReplaceWithUndefined({
      ...formValue,
      headquarters,
      auto_assignments,
    });
    const observable = {
      next: (_: unknown) => this._dialogRef.close(true),
      error: (error: any) => {
        this._baseComponentService.showErrorDialog({
          error: new ApiError(error),
        });
      },
    };
    const request = this.licenseId
      ? this._licenseService.patchLicense(this.licenseId, data)
      : this._licenseService.postLicense(data);
    request.subscribe(observable).add(() => (this.isPosting = false));
  }

  protected onDismiss(): void {
    this._dialogRef.close(false);
  }

  protected convertUserType(backend: string): string {
    return (
      {
        Student: 'Studenti',
        Teacher: 'Docenti',
        Parent: 'Genitori',
        Tutor: 'Tutori',
      }[backend] || ''
    );
  }

  protected addAutoAssignment(): void {
    (this.formGroup.get('auto_assignments') as FormArray).push(
      new FormGroup({
        headquarters: new FormArray(
          this.headquartersListing.map(() => new FormControl(false))
        ),
        user_types: new FormArray(
          this.userTypesListing.map(() => new FormControl(false))
        ),
        departments: new FormArray(
          this.departmentsListing.map(() => new FormControl(false))
        ),
        years: new FormArray([1, 2, 3, 4, 5].map(() => new FormControl(false))),
        sections: new FormArray(
          ['A', 'B', 'C'].map(() => new FormControl(false))
        ),
      })
    );
  }

  protected removeAutoAssignment(index: number): void {
    (this.formGroup.get('auto_assignments') as FormArray).removeAt(index);
  }
}
